import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router";
import { EmptyState, ErrorState, ForbiddenState, LoadingState } from "@/components/states";
import { PageHeader } from "@/components/bespoke";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ApiError } from "@/lib/api";
import {
  automationSettingsQuery,
  changeScheduleState,
  deliveriesQuery,
  schedulesQuery,
  retryDelivery,
  triggersQuery,
} from "@/lib/automation";
import { useCurrentProject } from "@/lib/project";
import { AutomationForm } from "./automation-form";
import { AutomationLimitsCard } from "./automation-limits";
import { formatZonedDateTime } from "@/lib/format";
function Heading() {
  return <PageHeader title="Automation" />;
}
const cell = "border-b px-4 py-3 text-left text-sm";
function Table({
  title,
  columns,
  children,
}: {
  title: string;
  columns: string[];
  children: React.ReactNode;
}) {
  return (
    <Card>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <caption className="sr-only">{title}</caption>
            <thead>
              <tr>
                {columns.map((column) => (
                  <th
                    key={column}
                    className={`${cell} text-xs font-medium text-muted-foreground`}
                    scope="col"
                  >
                    {column}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>{children}</tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
export function AutomationPage() {
  const { project, loaded } = useCurrentProject();
  const id = project?.id ?? "";
  const client = useQueryClient();
  const schedules = useQuery({ ...schedulesQuery(id), enabled: loaded && !!project });
  const triggers = useQuery({ ...triggersQuery(id), enabled: loaded && !!project });
  const deliveries = useQuery({ ...deliveriesQuery(id), enabled: loaded && !!project });
  const settings = useQuery({ ...automationSettingsQuery(id), enabled: loaded && !!project });
  const [form, setForm] = useState<"schedule" | "trigger" | null>(null);
  const [denied, setDenied] = useState(false);
  const [feedback, setFeedback] = useState("");
  const refresh = () => {
    for (const kind of ["schedules", "triggers", "deliveries"])
      void client.invalidateQueries({ queryKey: ["projects", id, kind] });
  };
  const change = useMutation({
    mutationFn: ({ ruleId, action }: { ruleId: string; action: "pause" | "resume" }) =>
      changeScheduleState(id, ruleId, action),
    onSuccess: () => {
      setFeedback("Schedule updated.");
      refresh();
    },
    onError: (error) => {
      if (error instanceof ApiError && [403, 404].includes(error.status)) {
        setDenied(true);
        void client.removeQueries({ queryKey: ["projects", id] });
      } else setFeedback("The schedule could not be updated. Try again.");
    },
  });
  const retry = useMutation({
    mutationFn: (deliveryId: string) => retryDelivery(id, deliveryId),
    onSuccess: () => {
      setFeedback("Delivery retried.");
      refresh();
    },
    onError: (error) => {
      if (error instanceof ApiError && [403, 404].includes(error.status)) setDenied(true);
      else setFeedback("The delivery could not be retried. Check the flow and project limits.");
    },
  });
  const queries = [schedules, triggers, deliveries, settings];
  const failure = queries.find((q) => q.isError)?.error;
  if (denied || (failure instanceof ApiError && [403, 404].includes(failure.status)))
    return (
      <>
        <Heading />
        <ForbiddenState
          title="You do not have access to this project"
          description="Ask an owner to invite you. Nothing from the project is shown."
        />
      </>
    );
  if (!loaded || (!!project && queries.some((q) => q.isPending)))
    return (
      <>
        <Heading />
        <LoadingState label="Loading automation" rows={5} />
      </>
    );
  if (!project)
    return (
      <>
        <Heading />
        <EmptyState title="No projects yet" description="Create a project to automate its runs." />
      </>
    );
  if (failure)
    return (
      <>
        <Heading />
        <ErrorState
          title="Automation could not be loaded"
          description="The request could not be completed."
          requestId={failure instanceof ApiError ? (failure.requestId ?? "unknown") : "unknown"}
          onRetry={() => {
            for (const query of queries) void query.refetch();
          }}
        />
      </>
    );
  const zone = settings.data!.timeZone;
  const canWrite = ["owner", "developer"].includes(project.role);
  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <Heading />
        {canWrite && (
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setForm(form === "trigger" ? null : "trigger")}
            >
              New trigger
            </Button>
            <Button onClick={() => setForm(form === "schedule" ? null : "schedule")}>
              New schedule
            </Button>
          </div>
        )}
      </div>
      {feedback && (
        <p role="status" className="text-sm text-muted-foreground">
          {feedback}
        </p>
      )}
      {form && canWrite && (
        <AutomationForm
          key={`${id}:${form}`}
          projectId={id}
          kind={form}
          onCreated={() => {
            setForm(null);
            setFeedback("Automation rule created.");
            refresh();
          }}
          onDenied={() => setDenied(true)}
        />
      )}
      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_19rem]">
        <div className="flex min-w-0 flex-col gap-6">
          <section className="grid gap-3" aria-labelledby="schedules-title">
            <div className="flex flex-wrap justify-between gap-2">
              <h2 id="schedules-title" className="text-base font-semibold">
                Schedules
              </h2>
              <span className="text-xs text-muted-foreground">{zone}, with UTC beneath</span>
            </div>
            {!schedules.data?.length ? (
              <EmptyState
                title="No schedules yet"
                description="Create a schedule to start a pinned flow on a timetable."
              />
            ) : (
              <Table
                title="Schedules"
                columns={["Schedule", "Flow", "Cadence", "Next run", "State", "Actions"]}
              >
                {schedules.data.map((rule) => (
                  <tr key={rule.id} className="hover:bg-muted/50">
                    <td className={`${cell} font-medium`}>{rule.name}</td>
                    <td className={`${cell} font-mono`}>
                      {rule.flowId.slice(0, 8)}@{rule.flowVersion}
                    </td>
                    <td className={`${cell} font-mono whitespace-nowrap`}>{rule.cron}</td>
                    <td className={`${cell} tabular-nums`}>
                      {rule.nextRunAt ? (
                        <>
                          <div>{formatZonedDateTime(rule.nextRunAt, zone)}</div>
                          <div className="text-xs text-muted-foreground">
                            {formatZonedDateTime(rule.nextRunAt, "UTC")} UTC
                          </div>
                        </>
                      ) : (
                        "Paused"
                      )}
                    </td>
                    <td className={cell}>
                      <Badge variant={rule.state === "active" ? "success" : "warning"}>
                        {rule.state === "active" ? "Active" : "Paused"}
                      </Badge>
                      {rule.state === "paused" && (
                        <p className="mt-1 text-xs text-muted-foreground">
                          {rule.consecutiveFailures >= 3
                            ? "After 3 consecutive failures"
                            : "Paused by a project member"}
                        </p>
                      )}
                    </td>
                    <td className={cell}>
                      {canWrite && (
                        <Button
                          size="sm"
                          variant="outline"
                          aria-label={`${rule.state === "active" ? "Pause" : "Resume"} ${rule.name}`}
                          disabled={change.isPending}
                          onClick={() =>
                            change.mutate({
                              ruleId: rule.id,
                              action: rule.state === "active" ? "pause" : "resume",
                            })
                          }
                        >
                          {rule.state === "active" ? "Pause" : "Resume"}
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </Table>
            )}
          </section>
          <section className="grid gap-3" aria-labelledby="triggers-title">
            <h2 id="triggers-title" className="text-base font-semibold">
              Triggers
            </h2>
            {!triggers.data?.length ? (
              <EmptyState
                title="No triggers yet"
                description="Register a signed webhook or a repository event."
              />
            ) : (
              <Table title="Triggers" columns={["Source", "Event", "Flow", "State"]}>
                {triggers.data.map((rule) => (
                  <tr key={rule.id} className="hover:bg-muted/50">
                    <td className={cell}>
                      {rule.source === "webhook"
                        ? "Webhook"
                        : `Repository event: ${rule.repositoryRef}`}
                    </td>
                    <td className={`${cell} font-mono`}>{rule.event}</td>
                    <td className={`${cell} font-mono`}>
                      {rule.flowId.slice(0, 8)}@{rule.flowVersion}
                    </td>
                    <td className={cell}>
                      <Badge variant={rule.state === "active" ? "success" : "warning"}>
                        {rule.state === "active"
                          ? "Active"
                          : rule.state === "paused"
                            ? "Paused"
                            : "Failing"}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </Table>
            )}
          </section>
          <section className="grid gap-3" aria-labelledby="deliveries-title">
            <div className="flex justify-between gap-2">
              <h2 id="deliveries-title" className="text-base font-semibold">
                Recent deliveries
              </h2>
              <span className="text-xs text-muted-foreground">Refreshes every 20 s</span>
            </div>
            {!deliveries.data?.length ? (
              <EmptyState
                title="No deliveries yet"
                description="Signed requests and rejected signatures appear here."
              />
            ) : (
              <Table
                title="Recent deliveries"
                columns={["Received", "Trigger", "Result", "Run", "Actions"]}
              >
                {deliveries.data.map((delivery) => (
                  <tr key={delivery.id} className="hover:bg-muted/50">
                    <td className={`${cell} tabular-nums`}>
                      {formatZonedDateTime(delivery.receivedAt, zone)}
                    </td>
                    <td className={`${cell} font-mono`}>
                      {delivery.triggerId?.slice(0, 8) ?? "Unknown"}
                    </td>
                    <td className={cell}>
                      <Badge
                        variant={
                          delivery.state === "started-run"
                            ? "success"
                            : delivery.state === "delivered" || delivery.state === "queued"
                              ? "info"
                              : delivery.state.startsWith("skipped")
                                ? "warning"
                                : "danger"
                        }
                      >
                        {
                          {
                            "started-run": "Started a run",
                            "rejected-bad-signature": "Signature invalid",
                            failed: "Failed",
                            delivered: "Delivered",
                            queued: "Queued",
                            "skipped-conditions": "Skipped by conditions",
                            "skipped-budget": "Skipped: over budget",
                            "skipped-duplicate": "Skipped: duplicate",
                            "skipped-quiet-hours": "Skipped: quiet hours",
                            "skipped-concurrency": "Skipped: busy",
                            "skipped-loop-guard": "Skipped: loop guard",
                            "skipped-approval": "Skipped: needs approval",
                            "skipped-policy": "Skipped: project policy",
                          }[delivery.state]
                        }
                      </Badge>
                      {delivery.reason && (
                        <p className="mt-1 text-xs text-muted-foreground">{delivery.reason}</p>
                      )}
                    </td>
                    <td className={cell}>
                      {delivery.runId ? (
                        <Link
                          className="font-mono underline focus-visible:outline-ring"
                          to={`/runs/${delivery.runId}`}
                        >
                          {delivery.runId.slice(0, 8)}
                        </Link>
                      ) : (
                        "None"
                      )}
                    </td>
                    <td className={cell}>
                      {canWrite && delivery.state !== "started-run" && (
                        <Button
                          size="sm"
                          variant="outline"
                          aria-label={`Retry delivery ${delivery.id.slice(0, 8)}`}
                          disabled={delivery.state !== "failed" || retry.isPending}
                          onClick={() => retry.mutate(delivery.id)}
                        >
                          Retry
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </Table>
            )}
          </section>
        </div>
        <aside className="flex flex-col gap-6">
          <Card>
            <CardContent>
              <h2 className="text-base font-semibold">Webhook endpoints</h2>
              <p className="mt-2 text-xs text-muted-foreground">
                Signed JSON must name its configured event. Repository rules also require the bound
                repositoryRef. Sign the timestamp, delivery id, and exact body. Extra provider
                fields never change the run configuration.
              </p>
              {triggers.data?.map((rule) => (
                <div key={rule.id} className="mt-4 grid gap-2 text-sm">
                  <h3 className="font-medium">{rule.event}</h3>
                  <code className="break-all rounded bg-muted p-2 text-xs">
                    /api/v1/projects/{id}/triggers/{rule.id}/webhook
                  </code>
                  <span>
                    Signing secret: {rule.hasSigningSecret ? "••••••••••••••••" : "Not configured"}
                  </span>
                </div>
              ))}
              <p className="mt-3 text-xs text-muted-foreground">
                Secrets are never revealed. A replay cannot start another run or reserve credits
                twice.
              </p>
            </CardContent>
          </Card>
          <AutomationLimitsCard
            projectId={id}
            isOwner={project.role === "owner"}
            runsAtOnce={settings.data!.concurrentRunLimit}
            perRunCap={settings.data!.perRunCap}
          />
        </aside>
      </div>
    </div>
  );
}
